import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth-compat';
import { isReleaseFlagOn } from '@/config/featureFlags';
import {
  addEnrollment,
  addEnrollmentByEmail,
  cancelSession,
  confirmEnrollment,
  createSession,
  deleteBroadcast,
  editBroadcast,
  fetchClassManagementHub,
  fetchMyClasses,
  fetchMyClassAttendance,
  fetchStudentClassSessions,
  fetchStudentClassBroadcasts,
  fetchTeacherBroadcasts,
  fetchOfferingEnrollments,
  fetchOfferingSessions,
  fetchSessionDetail,
  markAttendance,
  removeEnrollment,
  rescheduleSession,
  sendBroadcast,
  sendSessionSummary,
  upsertSessionNotes,
  upsertSessionSummary,
} from '@/services/classManagementService';

// F-3 — TanStack Query v5 hooks for class management

// ---- Hub ----

export function useClassManagementHub() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['class-management', 'hub'],
    queryFn: () => fetchClassManagementHub(token),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT'),
    staleTime: 30 * 1000,
  });
}

// ---- Enrollments ----

export function useOfferingEnrollments(offeringId: string, status?: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['class-management', 'enrollments', offeringId, status],
    queryFn: () => fetchOfferingEnrollments(token, offeringId, status),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT') && offeringId.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useAddEnrollment() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { threadId: string; offeringId: string }) =>
      addEnrollment(token, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'enrollments'] });
    },
  });
}

export function useAddEnrollmentByEmail() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { studentEmail: string; offeringId: string }) =>
      addEnrollmentByEmail(token, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'enrollments'] });
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'hub'] });
    },
  });
}

export function useConfirmEnrollment() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => confirmEnrollment(token, enrollmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'enrollments'] });
    },
  });
}

export function useRemoveEnrollment() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enrollmentId: string) => removeEnrollment(token, enrollmentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'enrollments'] });
    },
  });
}

// ---- Sessions ----

export function useOfferingSessions(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['class-management', 'sessions', offeringId],
    queryFn: () => fetchOfferingSessions(token, offeringId),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT') && offeringId.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useCreateSession() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      offeringId,
      scheduledStart,
      durationMinutes,
    }: {
      offeringId: string;
      scheduledStart: string;
      durationMinutes: number;
    }) => createSession(token, offeringId, { scheduledStart, durationMinutes }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['class-management', 'sessions', variables.offeringId],
      });
    },
  });
}

export function useSessionDetail(sessionId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['class-management', 'session', sessionId],
    queryFn: () => fetchSessionDetail(token, sessionId),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT') && sessionId.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useMarkAttendance() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      enrollmentId,
      status,
    }: {
      sessionId: string;
      enrollmentId: string;
      status: 'PRESENT' | 'ABSENT' | 'LATE';
    }) => markAttendance(token, sessionId, enrollmentId, { status }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['class-management', 'session', variables.sessionId],
      });
    },
  });
}

export function useUpsertNotes() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: string }) =>
      upsertSessionNotes(token, sessionId, { body }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['class-management', 'session', variables.sessionId],
      });
    },
  });
}

export function useUpsertSummary() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, body }: { sessionId: string; body: string }) =>
      upsertSessionSummary(token, sessionId, { body }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['class-management', 'session', variables.sessionId],
      });
    },
  });
}

export function useSendSummary() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => sendSessionSummary(token, sessionId),
    onSuccess: (_data, sessionId) => {
      void queryClient.invalidateQueries({
        queryKey: ['class-management', 'session', sessionId],
      });
    },
  });
}

export function useCancelSession(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, notifyStudents }: { sessionId: string; notifyStudents: boolean }) =>
      cancelSession(token, sessionId, notifyStudents),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'sessions', offeringId] });
    },
  });
}

export function useRescheduleSession(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      scheduledStart,
      durationMinutes,
      notifyStudents,
    }: {
      sessionId: string;
      scheduledStart: string;
      durationMinutes: number;
      notifyStudents: boolean;
    }) => rescheduleSession(token, sessionId, { scheduledStart, durationMinutes, notifyStudents }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'sessions', offeringId] });
    },
  });
}

// ---- Broadcasts ----

export function useSendBroadcast() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      offeringId,
      subject,
      body,
      expiresAt,
    }: {
      offeringId: string;
      subject: string;
      body: string;
      expiresAt?: string;
    }) => sendBroadcast(token, offeringId, { subject, body, expiresAt }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'broadcasts', variables.offeringId] });
    },
  });
}

export function useTeacherBroadcasts(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['class-management', 'broadcasts', offeringId],
    queryFn: () => fetchTeacherBroadcasts(token, offeringId),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT') && offeringId.length > 0,
    staleTime: 30 * 1000,
  });
}

export function useEditBroadcast() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ broadcastId, offeringId, subject, body, expiresAt }: {
      broadcastId: string;
      offeringId: string;
      subject: string;
      body: string;
      expiresAt?: string;
    }) => editBroadcast(token, broadcastId, { subject, body, expiresAt }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'broadcasts', variables.offeringId] });
    },
  });
}

export function useDeleteBroadcast() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ broadcastId, offeringId }: { broadcastId: string; offeringId: string }) =>
      deleteBroadcast(token, broadcastId),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['class-management', 'broadcasts', variables.offeringId] });
    },
  });
}

// ---- Student ----

export function useMyClasses() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['student', 'classes'],
    queryFn: () => fetchMyClasses(token),
    enabled: auth.isAuthenticated && isReleaseFlagOn('CLASS_MANAGEMENT'),
    staleTime: 30 * 1000,
  });
}

export function useStudentClassSessions(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['student', 'classes', offeringId, 'sessions'],
    queryFn: () => fetchStudentClassSessions(token, offeringId),
    enabled: auth.isAuthenticated && !!offeringId && isReleaseFlagOn('CLASS_MANAGEMENT'),
    staleTime: 60 * 1000,
  });
}

export function useStudentClassBroadcasts(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['student', 'classes', offeringId, 'broadcasts'],
    queryFn: () => fetchStudentClassBroadcasts(token, offeringId),
    enabled: auth.isAuthenticated && !!offeringId && isReleaseFlagOn('CLASS_MANAGEMENT'),
    staleTime: 60 * 1000,
  });
}

export function useMyClassAttendance(offeringId: string) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? '';
  return useQuery({
    queryKey: ['student', 'classes', offeringId, 'attendance'],
    queryFn: () => fetchMyClassAttendance(token, offeringId),
    enabled: auth.isAuthenticated && !!offeringId && isReleaseFlagOn('CLASS_MANAGEMENT'),
    staleTime: 2 * 60 * 1000,
  });
}
